import { useEffect, useState, useRef, useCallback } from "react";
import { Link, NavLink, Navigate, Routes, Route } from "react-router-dom";
import { LayoutDashboard, Home as HomeIcon, BedDouble, CalendarCheck, LogOut, IndianRupee, Building2, Users, MapPin, Tag, Layers, Menu, X, Upload, Eye, Plus, Trash2, Edit2, ClipboardList, BookOpen, Crown, Key, Tent } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, inr } from "@/lib/brand";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const NAV = [
  { to: "/admin", t: "Dashboard", i: LayoutDashboard, end: true },
  { to: "/admin/properties", t: "Properties", i: Building2 },
  { to: "/admin/rooms", t: "Rooms", i: BedDouble },
  { to: "/admin/bookings", t: "Bookings", i: CalendarCheck },
  { to: "/admin/listing-requests", t: "Listing Requests", i: ClipboardList },
  { to: "/admin/destinations", t: "Destinations", i: MapPin },
  { to: "/admin/property-types", t: "Property Types", i: Layers },
  { to: "/admin/offers", t: "Offers", i: Tag },
  { to: "/admin/users", t: "Users", i: Users },
  { to: "/admin/blogs", t: "Blogs", i: BookOpen },
  { to: "/admin/apartments", t: "Apartments", i: Key },
  { to: "/admin/memberships", t: "Memberships", i: Crown },
  { to: "/admin/adventures", t: "Adventures", i: Tent },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (user === undefined) return <div className="p-10">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" />;

  return (
    <div className="min-h-screen flex bg-stone-50" data-testid="admin-layout">
      {/* MOBILE TOPBAR */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-stone-900 text-white flex items-center justify-between px-4 py-3">
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="OLS" className="h-8 bg-white rounded p-1" />
          <span className="font-display text-sm">Admin</span>
        </Link>
        <button onClick={() => setOpen(!open)} className="p-1">{open ? <X /> : <Menu />}</button>
      </div>

      {/* SIDEBAR */}
      <aside className={`bg-[#0A0A0A] text-stone-300 w-60 shrink-0 fixed lg:sticky top-0 h-screen z-40 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} flex flex-col`}>
        <Link to="/" className="flex items-center gap-3 p-6">
          <img src={LOGO_URL} alt="OLS" className="h-10 bg-white rounded-lg p-1" />
          <div className="text-white font-display text-lg">Admin</div>
        </Link>
        <nav className="flex-1 px-3 space-y-1 text-sm overflow-y-auto">
          {NAV.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.end} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md ${isActive ? "bg-stone-800 text-white" : "hover:bg-stone-800/50"}`} data-testid={`admin-nav-${m.t.toLowerCase().replace(/\s/g, '-')}`}>
              <m.i className="w-4 h-4" /> {m.t}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-800 space-y-1 text-sm">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-stone-800/50"><HomeIcon className="w-4 h-4" /> View site</Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-stone-800/50 text-left" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>

      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 min-w-0">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="properties" element={<AdminProperties />} />
          <Route path="rooms" element={<AdminRooms />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="listing-requests" element={<AdminListingRequests />} />
          <Route path="destinations" element={<AdminDestinations />} />
          <Route path="property-types" element={<AdminPropertyTypes />} />
          <Route path="offers" element={<AdminOffers />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="blogs" element={<AdminBlogs />} />
          <Route path="apartments" element={<AdminApartments />} />
          <Route path="memberships" element={<AdminMemberships />} />
          <Route path="adventures" element={<AdminAdventures />} />
        </Routes>
      </main>
    </div>
  );
}

// =============================================================================
// DASHBOARD
// =============================================================================
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-stone-500">Loading…</div>;
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-2xl md:text-3xl mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <Stat icon={IndianRupee} label="Total Revenue" value={inr(stats.total_revenue)} />
        <Stat icon={CalendarCheck} label="Bookings" value={stats.total_bookings} />
        <Stat icon={Building2} label="Properties" value={stats.active_properties} />
        <Stat icon={Users} label="Customers" value={stats.total_customers} />
      </div>
      <div className="bg-white border border-stone-200 rounded-xl p-4 md:p-6">
        <div className="font-display text-lg md:text-xl mb-4">Monthly Revenue</div>
        {stats.monthly_revenue.length === 0 ? <div className="text-sm text-stone-500">No bookings yet</div> : (
          <div className="grid grid-cols-12 gap-2 items-end h-40 md:h-48">
            {stats.monthly_revenue.map((m) => {
              const max = Math.max(...stats.monthly_revenue.map(x => x.revenue));
              const h = max ? (m.revenue / max) * 100 : 0;
              return (
                <div key={m.month} className="flex flex-col items-center">
                  <div className="w-full bg-stone-900 rounded-t" style={{ height: `${h}%` }} title={inr(m.revenue)} />
                  <div className="text-[0.6rem] text-stone-500 mt-2">{m.month.slice(5)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="mt-4 text-sm text-stone-600">Occupancy rate: <b>{stats.occupancy_rate}%</b></div>
    </div>
  );
}
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 md:p-5">
      <Icon className="w-5 h-5 text-stone-700 mb-2" />
      <div className="text-[0.65rem] text-stone-500 uppercase tracking-wider">{label}</div>
      <div className="font-display text-xl md:text-2xl mt-1">{value}</div>
    </div>
  );
}

// =============================================================================
// IMAGE UPLOADER (used in editors)
// =============================================================================
function ImageUploader({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const list = (value || "").split("\n").map(s => s.trim()).filter(Boolean);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const upload = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(BACKEND + data.url);
      }
      onChange([...list, ...urls].join("\n"));
    } catch (e) { alert(formatError(e)); }
    finally { setUploading(false); }
  };
  const remove = (i) => { const nl = list.filter((_, idx) => idx !== i); onChange(nl.join("\n")); };

  return (
    <div>
      <div className="ols-label mb-1">Images</div>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-2">
        {list.map((u, i) => (
          <div key={i} className="relative aspect-square border border-stone-200 rounded-md overflow-hidden bg-stone-50">
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full text-[10px] flex items-center justify-center">×</button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} className="aspect-square border-2 border-dashed border-stone-300 rounded-md flex flex-col items-center justify-center text-xs text-stone-500 hover:border-stone-500" data-testid={testid}>
          {uploading ? "Uploading…" : <><Upload className="w-4 h-4 mb-1" />Upload</>}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="w-full border border-stone-300 px-3 py-2 text-xs outline-none rounded-md mt-1" placeholder="Or paste image URLs, one per line" />
    </div>
  );
}

// =============================================================================
// PROPERTIES
// =============================================================================
function AdminProperties() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/properties?limit=200").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/properties/${id}`); load(); };
  return (
    <div data-testid="admin-properties">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Properties</h1>
        <button onClick={() => setEditing({})} className="btn-primary text-sm" data-testid="add-property"><Plus className="w-4 h-4" /> Add property</button>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Destination</th><th className="text-left p-3">Type</th><th className="text-left p-3">From</th><th className="text-left p-3">GST</th><th className="text-left p-3">Status</th><th className="text-left p-3">Reserve Link</th><th /></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-stone-100">
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3">{p.destination}</td>
                <td className="p-3 capitalize">{p.property_type}</td>
                <td className="p-3">{inr(p.starting_price)}</td>
                <td className="p-3">{p.gst_percent ?? 12}%</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs">{p.status}</span></td>
                <td className="p-3">
                  {p.reserve_link ? (
                    <a href={p.reserve_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline truncate max-w-[140px] inline-block" title={p.reserve_link}>✔ Set</a>
                  ) : (
                    <span className="text-xs text-stone-400">—</span>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className="text-stone-700 mr-3" data-testid={`edit-property-${p.id}`}><Edit2 className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(p.id)} className="text-red-700" data-testid={`delete-property-${p.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PropertyEditor item={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PropertyEditor({ item, onClose }) {
  const isNew = !item.id;
  const [destinations, setDestinations] = useState([]);
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({
    title: item.title || "",
    destination: item.destination || "",
    property_type: item.property_type || "villa",
    description: item.description || "",
    location: item.location || "",
    latitude: item.latitude || 0,
    longitude: item.longitude || 0,
    starting_price: item.starting_price || 100,
    gst_percent: item.gst_percent ?? 12,
    images: (item.images || []).join("\n"),
    amenities: (item.amenities || []).join(", "),
    is_featured: item.is_featured || false,
    status: item.status || "active",
    highlights: (item.highlights || []).join("\n"),
    policies: (item.policies || []).join("\n"),
    house_rules: (item.house_rules || []).join("\n"),
    faqs: item.faqs || [],
    reserve_link: item.reserve_link || "",
    member_pricing_enabled: item.member_pricing_enabled || false,
    member_pricing_percent: item.member_pricing_percent || 0,
  });
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/destinations").then((r) => setDestinations(r.data));
    api.get("/property-types").then((r) => setTypes(r.data));
  }, []);

  const updateFaq = (i, k, v) => { const n = [...f.faqs]; n[i] = { ...n[i], [k]: v }; setF({ ...f, faqs: n }); };
  const addFaq = () => setF({ ...f, faqs: [...f.faqs, { q: "", a: "" }] });
  const delFaq = (i) => setF({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...f,
      starting_price: +f.starting_price, gst_percent: +f.gst_percent,
      member_pricing_percent: +f.member_pricing_percent || 0,
      latitude: +f.latitude, longitude: +f.longitude,
      images: f.images.split("\n").map(s => s.trim()).filter(Boolean),
      amenities: f.amenities.split(",").map(s => s.trim()).filter(Boolean),
      highlights: f.highlights.split("\n").map(s => s.trim()).filter(Boolean),
      policies: f.policies.split("\n").map(s => s.trim()).filter(Boolean),
      house_rules: f.house_rules.split("\n").map(s => s.trim()).filter(Boolean),
      faqs: f.faqs.filter(x => x.q && x.a),
    };
    try {
      if (isNew) await api.post("/admin/properties", payload);
      else await api.put(`/admin/properties/${item.id}`, payload);
      onClose();
    } catch (e) { setErr(formatError(e)); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="property-editor">
        <div className="font-display text-xl md:text-2xl">{isNew ? "Add Property" : "Edit Property"}</div>
        {err && <div className="text-red-700 text-sm">{err}</div>}
        <Field label="Title" v={f.title} on={(v) => setF({ ...f, title: v })} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select label="Destination" v={f.destination} on={(v) => setF({ ...f, destination: v })} options={[...new Set([...destinations.map(d => d.name), f.destination].filter(Boolean))]} placeholder="Select destination" />
          <Select label="Property Type" v={f.property_type} on={(v) => setF({ ...f, property_type: v })} options={[...new Set([...(types.length ? types.map(t => t.name.toLowerCase()) : ["villa", "resort", "homestay", "cottage", "hotel"]), f.property_type?.toLowerCase()].filter(Boolean))]} />
        </div>
        <Field label="Location" v={f.location} on={(v) => setF({ ...f, location: v })} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Latitude" type="number" v={f.latitude} on={(v) => setF({ ...f, latitude: v })} />
          <Field label="Longitude" type="number" v={f.longitude} on={(v) => setF({ ...f, longitude: v })} />
          <Field label="Starting Price ₹" type="number" v={f.starting_price} on={(v) => setF({ ...f, starting_price: v })} />
          <Field label="GST %" type="number" v={f.gst_percent} on={(v) => setF({ ...f, gst_percent: v })} />
        </div>
        <Field label="Description" v={f.description} on={(v) => setF({ ...f, description: v })} textarea />
        <div>
          <Field label="Reserve Link (optional)" v={f.reserve_link} on={(v) => setF({ ...f, reserve_link: v })} />
          <p className="text-xs text-stone-500 mt-1">If provided, the <strong>Reserve</strong> button on the property page will redirect customers to this URL (opens in new tab) instead of the checkout flow. Leave blank to use normal checkout.</p>
        </div>

        {/* Member Pricing */}
        <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3" data-testid="member-pricing-section">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={f.member_pricing_enabled}
              onChange={(e) => setF({ ...f, member_pricing_enabled: e.target.checked })}
              className="w-4 h-4"
              data-testid="member-pricing-enabled"
            />
            <span className="font-semibold text-sm">Members Only Pricing</span>
          </label>
          <p className="text-xs text-stone-500 mt-1 ml-6">
            When enabled, active members see a discounted price on this property's rooms. Non-members see the regular price with a nudge to become a member.
          </p>
          {f.member_pricing_enabled && (
            <div className="mt-3 ml-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Global discount % (fallback)"
                type="number"
                v={f.member_pricing_percent}
                on={(v) => setF({ ...f, member_pricing_percent: v })}
              />
              <div className="text-xs text-stone-600 self-end pb-2">
                Applied to any room without a specific "Member Price" set. e.g. <strong>10</strong> = 10% off.
              </div>
            </div>
          )}
        </div>
        <ImageUploader value={f.images} onChange={(v) => setF({ ...f, images: v })} testid="property-image-upload" />
        <Field label="Amenities (comma-separated)" v={f.amenities} on={(v) => setF({ ...f, amenities: v })} />
        <Field label="Highlights (one per line)" v={f.highlights} on={(v) => setF({ ...f, highlights: v })} textarea />
        <Field label="Policies (one per line)" v={f.policies} on={(v) => setF({ ...f, policies: v })} textarea />
        <Field label="House Rules (one per line)" v={f.house_rules} on={(v) => setF({ ...f, house_rules: v })} textarea />

        {/* FAQs editor */}
        <div className="border border-stone-200 rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="ols-label">FAQs</div>
            <button type="button" onClick={addFaq} className="text-xs text-blue-600 underline" data-testid="add-faq">+ Add FAQ</button>
          </div>
          <div className="space-y-2">
            {f.faqs.map((faq, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start">
                <input value={faq.q} onChange={(e) => updateFaq(i, "q", e.target.value)} placeholder="Question" className="border border-stone-300 rounded px-2 py-1.5 text-sm outline-none" />
                <input value={faq.a} onChange={(e) => updateFaq(i, "a", e.target.value)} placeholder="Answer" className="border border-stone-300 rounded px-2 py-1.5 text-sm outline-none" />
                <button type="button" onClick={() => delFaq(i)} className="text-red-700 text-xs px-2">Remove</button>
              </div>
            ))}
            {f.faqs.length === 0 && <div className="text-xs text-stone-500">No FAQs yet.</div>}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_featured} onChange={(e) => setF({ ...f, is_featured: e.target.checked })} /> Featured on home page</label>
        <div className="flex gap-2 justify-end pt-3">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary" data-testid="save-property">Save</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// ROOMS
// =============================================================================
function AdminRooms() {
  const [props, setProps] = useState([]);
  const [propId, setPropId] = useState("");
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/properties?limit=200").then((r) => { setProps(r.data); if (r.data[0]) setPropId(r.data[0].id); }); }, []);
  const load = useCallback(() => { if (propId) api.get("/admin/rooms", { params: { property_id: propId } }).then((r) => setRooms(r.data)); }, [propId]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Delete room?")) return; await api.delete(`/admin/rooms/${id}`); load(); };
  return (
    <div data-testid="admin-rooms">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Rooms</h1>
        <button onClick={() => setEditing({ property_id: propId })} className="btn-primary text-sm" data-testid="add-room"><Plus className="w-4 h-4" /> Add room</button>
      </div>
      <div className="mb-4">
        <select value={propId} onChange={(e) => setPropId(e.target.value)} className="border border-stone-300 rounded-md px-3 py-2 text-sm w-full md:w-auto">
          {props.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr><th className="text-left p-3">Room</th><th className="text-left p-3">Size</th><th className="text-left p-3">Capacity</th><th className="text-left p-3">Price</th><th /></tr>
          </thead>
          <tbody>
            {rooms.map((r) => (
              <tr key={r.id} className="border-t border-stone-100">
                <td className="p-3 font-medium">{r.room_name}</td>
                <td className="p-3">{r.room_size}</td>
                <td className="p-3">{r.max_adults}A / {r.max_children}C</td>
                <td className="p-3">{inr(r.price_per_night)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} className="text-stone-700 mr-3"><Edit2 className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(r.id)} className="text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {rooms.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-stone-500">No rooms yet</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <RoomEditor item={editing} properties={props} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function RoomEditor({ item, properties, onClose }) {
  const isNew = !item.id;
  const [f, setF] = useState({
    property_id: item.property_id || properties[0]?.id || "",
    room_name: item.room_name || "",
    description: item.description || "",
    max_adults: item.max_adults || 2,
    max_children: item.max_children || 0,
    room_size: item.room_size || "",
    price_per_night: item.price_per_night || 100,
    total_rooms: item.total_rooms || 5,
    member_price_per_night: item.member_price_per_night || "",
    images: (item.images || []).join("\n"),
    amenities: (item.amenities || []).join(", "),
  });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...f, max_adults: +f.max_adults, max_children: +f.max_children,
      price_per_night: +f.price_per_night, total_rooms: +f.total_rooms,
      member_price_per_night: f.member_price_per_night === "" || f.member_price_per_night === null ? null : +f.member_price_per_night,
      images: f.images.split("\n").map(s => s.trim()).filter(Boolean),
      amenities: f.amenities.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/admin/rooms", payload);
      else await api.put(`/admin/rooms/${item.id}`, payload);
      onClose();
    } catch (e) { setErr(formatError(e)); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="room-editor">
        <div className="font-display text-xl md:text-2xl">{isNew ? "Add Room" : "Edit Room"}</div>
        {err && <div className="text-red-700 text-sm">{err}</div>}
        <Select label="Property" v={f.property_id} on={(v) => setF({ ...f, property_id: v })} options={properties.map(p => ({ value: p.id, label: p.title }))} />
        <Field label="Room name" v={f.room_name} on={(v) => setF({ ...f, room_name: v })} />
        <Field label="Description" v={f.description} on={(v) => setF({ ...f, description: v })} textarea />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Adults" type="number" v={f.max_adults} on={(v) => setF({ ...f, max_adults: v })} />
          <Field label="Children" type="number" v={f.max_children} on={(v) => setF({ ...f, max_children: v })} />
          <Field label="Size" v={f.room_size} on={(v) => setF({ ...f, room_size: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price/night ₹" type="number" v={f.price_per_night} on={(v) => setF({ ...f, price_per_night: v })} />
          <Field label="Total rooms" type="number" v={f.total_rooms} on={(v) => setF({ ...f, total_rooms: v })} />
        </div>
        <div>
          <Field
            label="Member Price / night ₹ (optional — overrides property % for this room)"
            type="number"
            v={f.member_price_per_night}
            on={(v) => setF({ ...f, member_price_per_night: v })}
          />
          <p className="text-xs text-stone-500 mt-1">Leave blank to use the property's global member discount %. Only applies if the parent property has "Members Only Pricing" enabled.</p>
        </div>
        <ImageUploader value={f.images} onChange={(v) => setF({ ...f, images: v })} testid="room-image-upload" />
        <Field label="Amenities (comma-separated)" v={f.amenities} on={(v) => setF({ ...f, amenities: v })} />
        <div className="flex gap-2 justify-end pt-3">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary" data-testid="save-room">Save</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// BOOKINGS
// =============================================================================
function AdminBookings() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get("/admin/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, field, value) => { await api.put(`/admin/bookings/${id}`, { [field]: value }); load(); };
  return (
    <div data-testid="admin-bookings">
      <h1 className="font-display text-2xl md:text-3xl mb-6">Bookings</h1>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr><th className="text-left p-3">#</th><th className="text-left p-3">Guest</th><th className="text-left p-3">Property</th><th className="text-left p-3">Dates</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Payment</th><th /></tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-stone-100" data-testid={`admin-booking-${b.id}`}>
                <td className="p-3 font-mono text-xs">{b.booking_number}</td>
                <td className="p-3">
                  <div>{b.user_name}</div>
                  <div className="text-xs text-stone-500">{b.user_email}</div>
                  <div className="text-xs text-stone-500">{b.guest_phone || "—"}</div>
                </td>
                <td className="p-3">{b.property_title}<div className="text-xs text-stone-500">{b.room_name}</div></td>
                <td className="p-3 text-xs">{b.checkin}<br />{b.checkout}</td>
                <td className="p-3">{inr(b.amount)}</td>
                <td className="p-3">
                  <select value={b.status} onChange={(e) => update(b.id, "status", e.target.value)} className="border border-stone-300 rounded px-2 py-1 text-xs">
                    {["confirmed", "completed", "cancelled"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <select value={b.payment_status} onChange={(e) => update(b.id, "payment_status", e.target.value)} className="border border-stone-300 rounded px-2 py-1 text-xs">
                    {["pending", "paid", "refunded"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <button onClick={() => setView(b)} className="text-stone-700 underline text-xs" data-testid={`view-booking-${b.id}`}><Eye className="w-4 h-4 inline" /> View</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-stone-500">No bookings yet</td></tr>}
          </tbody>
        </table>
      </div>
      {view && <BookingDetail booking={view} onClose={() => setView(null)} />}
    </div>
  );
}

function BookingDetail({ booking, onClose }) {
  return (
    <Modal onClose={onClose}>
      <div className="space-y-4" data-testid="booking-detail-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-xl md:text-2xl">{booking.property_title}</div>
            <div className="text-xs font-mono text-stone-500 mt-1">{booking.booking_number}</div>
          </div>
          <span className={`px-2 py-1 rounded text-xs ${booking.status === "cancelled" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{booking.status}</span>
        </div>
        {booking.property_image && <img src={booking.property_image} alt="" className="w-full h-40 object-cover rounded-lg" />}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <KV label="Guest Name" value={booking.guest_name || booking.user_name} />
          <KV label="Email" value={booking.user_email || booking.guest_email} />
          <KV label="Phone" value={booking.guest_phone || "—"} />
          <KV label="Number of Guests" value={booking.guests} />
          <KV label="Number of Rooms" value={booking.rooms_count || 1} />
          <KV label="Room Type" value={booking.room_name} />
          <KV label="Check-in" value={booking.checkin} />
          <KV label="Check-out" value={booking.checkout} />
          <KV label="Nights" value={booking.nights} />
          <KV label="Payment Method" value={booking.payment_method === "pay_at_reception" ? "Pay at Reception" : booking.payment_method} />
          <KV label="Payment Status" value={booking.payment_status} />
        </div>
        <div className="border-t border-stone-200 pt-3">
          <div className="font-semibold text-sm mb-2">Pricing</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-stone-600"><span>Price/night × nights × rooms</span><span>{inr(booking.subtotal)}</span></div>
            <div className="flex justify-between text-stone-600"><span>GST ({booking.gst_percent || 12}%)</span><span>{inr(booking.taxes)}</span></div>
            <div className="flex justify-between font-semibold pt-2 border-t border-stone-100"><span>Total</span><span>{inr(booking.amount)}</span></div>
          </div>
        </div>
        <div className="flex justify-end pt-2"><button onClick={onClose} className="btn-outline">Close</button></div>
      </div>
    </Modal>
  );
}

// =============================================================================
// DESTINATIONS
// =============================================================================
function AdminDestinations() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/destinations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/destinations/${id}`); load(); };
  return (
    <div data-testid="admin-destinations">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Destinations</h1>
        <button onClick={() => setEditing({})} className="btn-primary text-sm"><Plus className="w-4 h-4" /> Add destination</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((d) => (
          <div key={d.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <img src={d.image} alt={d.name} className="w-full h-32 object-cover" />
            <div className="p-3">
              <div className="font-semibold">{d.name}</div>
              <div className="text-xs text-stone-500">{d.property_count || 0} stays</div>
              <div className="mt-2 flex gap-2 text-xs">
                <button onClick={() => setEditing(d)} className="text-stone-700"><Edit2 className="w-3.5 h-3.5 inline" /> Edit</button>
                <button onClick={() => del(d.id)} className="text-red-700"><Trash2 className="w-3.5 h-3.5 inline" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <DestinationEditor item={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}
function DestinationEditor({ item, onClose }) {
  const isNew = !item.id;
  const [f, setF] = useState({ name: item.name || "", image: item.image || "", description: item.description || "", property_count: item.property_count || 0 });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, property_count: +f.property_count };
      if (isNew) await api.post("/admin/destinations", payload);
      else await api.put(`/admin/destinations/${item.id}`, payload);
      onClose();
    } catch (e) { setErr(formatError(e)); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="font-display text-xl">{isNew ? "Add Destination" : "Edit Destination"}</div>
        {err && <div className="text-red-700 text-sm">{err}</div>}
        <Field label="Name" v={f.name} on={(v) => setF({ ...f, name: v })} />
        <ImageUploader value={f.image} onChange={(v) => setF({ ...f, image: v.split("\n")[0] || "" })} />
        <Field label="Description" v={f.description} on={(v) => setF({ ...f, description: v })} textarea />
        <Field label="Property count" type="number" v={f.property_count} on={(v) => setF({ ...f, property_count: v })} />
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary">Save</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// PROPERTY TYPES
// =============================================================================
function AdminPropertyTypes() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/property-types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); if (!name) return; await api.post("/admin/property-types", { name }); setName(""); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/property-types/${id}`); load(); };
  return (
    <div data-testid="admin-property-types">
      <h1 className="font-display text-2xl md:text-3xl mb-6">Property Types</h1>
      <form onSubmit={add} className="flex gap-2 mb-6 max-w-md">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Villa" className="flex-1 border border-stone-300 rounded-md px-3 py-2 text-sm outline-none" data-testid="new-type-name" />
        <button className="btn-primary text-sm" data-testid="add-type">Add</button>
      </form>
      <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100">
        {items.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-3 text-sm">
            <span>{t.name}</span>
            <button onClick={() => del(t.id)} className="text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-center text-stone-500 text-sm">No property types yet — using default list.</div>}
      </div>
    </div>
  );
}

// =============================================================================
// OFFERS
// =============================================================================
function AdminOffers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/offers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/offers/${id}`); load(); };
  return (
    <div data-testid="admin-offers">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Offers</h1>
        <button onClick={() => setEditing({})} className="btn-primary text-sm"><Plus className="w-4 h-4" /> Add Offer</button>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr><th className="text-left p-3">Bank/Brand</th><th className="text-left p-3">Title</th><th className="text-left p-3">Code</th><th className="text-left p-3">Active</th><th /></tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-t border-stone-100">
                <td className="p-3 font-medium">{o.bank}</td>
                <td className="p-3">{o.title}</td>
                <td className="p-3 font-mono text-xs">{o.code}</td>
                <td className="p-3">{o.active ? "Yes" : "No"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(o)} className="text-stone-700 mr-3"><Edit2 className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(o.id)} className="text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-stone-500">No offers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <OfferEditor item={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}
function OfferEditor({ item, onClose }) {
  const isNew = !item.id;
  const [f, setF] = useState({ bank: item.bank || "", title: item.title || "", sub: item.sub || "", code: item.code || "", color: item.color || "from-rose-50 to-amber-50", active: item.active ?? true });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      if (isNew) await api.post("/admin/offers", f);
      else await api.put(`/admin/offers/${item.id}`, f);
      onClose();
    } catch (e) { setErr(formatError(e)); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="offer-editor">
        <div className="font-display text-xl">{isNew ? "Add Offer" : "Edit Offer"}</div>
        {err && <div className="text-red-700 text-sm">{err}</div>}
        <Field label="Bank / Brand" v={f.bank} on={(v) => setF({ ...f, bank: v })} />
        <Field label="Title (e.g. Get 11% off, upto ₹5000)" v={f.title} on={(v) => setF({ ...f, title: v })} />
        <Field label="Subtitle / conditions" v={f.sub} on={(v) => setF({ ...f, sub: v })} />
        <Field label="Promo code" v={f.code} on={(v) => setF({ ...f, code: v })} />
        <Select label="Background gradient" v={f.color} on={(v) => setF({ ...f, color: v })} options={[
          { value: "from-red-50 to-orange-50", label: "Red → Orange" },
          { value: "from-blue-50 to-indigo-50", label: "Blue → Indigo" },
          { value: "from-amber-50 to-yellow-50", label: "Amber → Yellow" },
          { value: "from-rose-50 to-amber-50", label: "Rose → Amber" },
          { value: "from-emerald-50 to-teal-50", label: "Emerald → Teal" },
        ]} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (visible on home page)</label>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary" data-testid="save-offer">Save</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// USERS
// =============================================================================
function AdminUsers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; await api.delete(`/admin/users/${id}`); load(); };
  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Users</h1>
        <button onClick={() => setEditing({})} className="btn-primary text-sm"><Plus className="w-4 h-4" /> Add user</button>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Role</th><th /></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-stone-100">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.phone || "—"}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded bg-stone-100 text-xs">{u.role}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(u)} className="text-stone-700 mr-3" data-testid={`edit-user-${u.id}`}><Edit2 className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(u.id)} className="text-red-700" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-stone-500">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <UserEditor item={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}
function UserEditor({ item, onClose }) {
  const isNew = !item.id;
  const [f, setF] = useState({ name: item.name || "", email: item.email || "", phone: item.phone || "", role: item.role || "user", password: "" });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f };
      if (!payload.password && !isNew) delete payload.password;
      if (isNew) await api.post("/admin/users", payload);
      else await api.put(`/admin/users/${item.id}`, payload);
      onClose();
    } catch (e) { setErr(formatError(e)); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="user-editor">
        <div className="font-display text-xl">{isNew ? "Add User" : "Edit User"}</div>
        {err && <div className="text-red-700 text-sm">{err}</div>}
        <Field label="Full Name" v={f.name} on={(v) => setF({ ...f, name: v })} />
        <Field label="Email" type="email" v={f.email} on={(v) => setF({ ...f, email: v })} />
        <Field label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} />
        <Select label="Role" v={f.role} on={(v) => setF({ ...f, role: v })} options={["user", "admin"]} />
        <Field label={isNew ? "Password" : "New password (leave blank to keep)"} type="password" v={f.password} on={(v) => setF({ ...f, password: v })} />
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary" data-testid="save-user">Save</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// LISTING REQUESTS (Partner sign-ups)
// =============================================================================
function AdminListingRequests() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/partner-listings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this listing request?")) return; await api.delete(`/admin/partner-listings/${id}`); load(); };
  const updatePayment = async (id, payment_status) => { await api.put(`/admin/partner-listings/${id}`, { payment_status }); load(); };

  const PLAN_LABEL = { startup: "Startup", plus: "Plus", executive: "Executive" };
  const PAY_COLOR = { Pending: "bg-amber-50 text-amber-700", Paid: "bg-emerald-50 text-emerald-700", Cancelled: "bg-stone-100 text-stone-600", Refunded: "bg-red-50 text-red-700" };

  return (
    <div data-testid="admin-listing-requests">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Listing Requests</h1>
        <div className="text-sm text-stone-500">{items.length} total</div>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Property</th>
              <th className="text-left p-3">Rooms</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Fee</th>
              <th className="text-left p-3">Payment</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-stone-100" data-testid={`listing-${p.id}`}>
                <td className="p-3 font-mono text-xs">{p.request_number}</td>
                <td className="p-3">
                  <div className="font-medium">{p.full_name}</div>
                  <div className="text-xs text-stone-500">{p.email}</div>
                  <div className="text-xs text-stone-500">{p.phone}</div>
                </td>
                <td className="p-3">
                  <div className="font-medium">{p.property_name}</div>
                  <div className="text-xs text-stone-500">{p.city}, {p.country}</div>
                </td>
                <td className="p-3">{p.rooms_count}</td>
                <td className="p-3">{PLAN_LABEL[p.plan] || p.plan}</td>
                <td className="p-3">{inr(p.plan_fee)} <span className="text-[0.6rem] text-stone-500">{p.currency}</span></td>
                <td className="p-3">
                  <select value={p.payment_status} onChange={(e) => updatePayment(p.id, e.target.value)} className={`border border-stone-300 rounded px-2 py-1 text-xs ${PAY_COLOR[p.payment_status] || ""}`} data-testid={`payment-status-${p.id}`}>
                    {["Pending", "Paid", "Cancelled", "Refunded"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setView(p)} className="text-stone-700 mr-2" data-testid={`view-listing-${p.id}`} title="View"><Eye className="w-4 h-4 inline" /></button>
                  <button onClick={() => setEditing(p)} className="text-stone-700 mr-2" data-testid={`edit-listing-${p.id}`} title="Edit"><Edit2 className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(p.id)} className="text-red-700" data-testid={`delete-listing-${p.id}`} title="Delete"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-stone-500">No listing requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {view && <ListingRequestView request={view} onClose={() => { setView(null); load(); }} onUpdatePayment={updatePayment} />}
      {editing && <ListingRequestEditor request={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ListingRequestView({ request, onClose, onUpdatePayment }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <Modal onClose={onClose}>
      <div className="space-y-4" data-testid="listing-view-modal">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-xl md:text-2xl">{request.property_name}</div>
            <div className="text-xs font-mono text-stone-500 mt-1">{request.request_number}</div>
          </div>
          <select defaultValue={request.payment_status} onChange={(e) => onUpdatePayment(request.id, e.target.value)} className="border border-stone-300 rounded px-2 py-1 text-xs">
            {["Pending", "Paid", "Cancelled", "Refunded"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <KV label="Full Name" value={request.full_name} />
          <KV label="Email" value={request.email} />
          <KV label="Phone" value={request.phone} />
          <KV label="Property Location" value={request.property_location} />
          <KV label="Address" value={request.address} />
          <KV label="City / Country" value={`${request.city}, ${request.country}`} />
          <KV label="Number of Rooms" value={request.rooms_count} />
          <KV label="Currency" value={request.currency} />
          <KV label="Plan" value={`${request.plan} — ${inr(request.plan_fee)}`} />
          <KV label="Agreed Terms" value={request.agreed_terms ? "Yes" : "No"} />
          <KV label="Submitted At" value={new Date(request.created_at).toLocaleString()} />
        </div>
        <div className="border-t border-stone-200 pt-4">
          <div className="font-semibold text-sm mb-3">Property Photos <span className="text-stone-500 font-normal">({request.images?.length || 0})</span></div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 max-h-96 overflow-y-auto" data-testid="listing-photos">
            {(request.images || []).map((u, i) => {
              const full = u.startsWith("http") ? u : BACKEND + u;
              return (
                <a key={i} href={full} target="_blank" rel="noreferrer" className="aspect-square rounded-md overflow-hidden border border-stone-200 hover:scale-105 transition">
                  <img src={full} alt={`property-${i}`} className="w-full h-full object-cover" />
                </a>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end pt-2"><button onClick={onClose} className="btn-outline">Close</button></div>
      </div>
    </Modal>
  );
}

function ListingRequestEditor({ request, onClose }) {
  const [f, setF] = useState({
    full_name: request.full_name || "", email: request.email || "", phone: request.phone || "",
    property_location: request.property_location || "", property_name: request.property_name || "",
    address: request.address || "", rooms_count: request.rooms_count || 1,
    city: request.city || "", country: request.country || "", currency: request.currency || "INR",
    plan: request.plan || "plus", payment_status: request.payment_status || "Pending",
    notes: request.notes || "",
  });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/partner-listings/${request.id}`, { ...f, rooms_count: +f.rooms_count });
      onClose();
    } catch (e) { setErr(formatError(e)); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="listing-editor">
        <div className="font-display text-xl">Edit Listing Request</div>
        {err && <div className="text-red-700 text-sm">{err}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Full Name" v={f.full_name} on={(v) => setF({ ...f, full_name: v })} />
          <Field label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} />
          <Field label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} />
          <Field label="Property Name" v={f.property_name} on={(v) => setF({ ...f, property_name: v })} />
        </div>
        <Field label="Property Location" v={f.property_location} on={(v) => setF({ ...f, property_location: v })} textarea />
        <Field label="Address" v={f.address} on={(v) => setF({ ...f, address: v })} textarea />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Rooms" type="number" v={f.rooms_count} on={(v) => setF({ ...f, rooms_count: v })} />
          <Field label="City" v={f.city} on={(v) => setF({ ...f, city: v })} />
          <Field label="Country" v={f.country} on={(v) => setF({ ...f, country: v })} />
          <Field label="Currency" v={f.currency} on={(v) => setF({ ...f, currency: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Plan" v={f.plan} on={(v) => setF({ ...f, plan: v })} options={["startup", "plus", "executive"]} />
          <Select label="Payment Status" v={f.payment_status} on={(v) => setF({ ...f, payment_status: v })} options={["Pending", "Paid", "Cancelled", "Refunded"]} />
        </div>
        <Field label="Internal Notes" v={f.notes} on={(v) => setF({ ...f, notes: v })} textarea />
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary" data-testid="save-listing">Save</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// BLOGS
// =============================================================================
function AdminBlogs() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .get("/blogs?limit=200")
      .then((r) => setItems(r.data))
      .catch(() => { });

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await api.delete(`/admin/blogs/${id}`);
      load();
    } catch (e) { alert(formatError(e)); }
  };

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Blogs</h1>
        <button
          onClick={() => setEditing({})}
          className="btn-primary text-sm"
          data-testid="add-blog"
        >
          <Plus className="w-4 h-4" /> Add post
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-stone-50 text-stone-600">
            <tr>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-stone-400 text-sm">
                  No blog posts yet. Click "Add post" to create one.
                </td>
              </tr>
            )}
            {items.map((b) => (
              <tr key={b.id} className="border-t border-stone-100">
                <td className="p-3 font-medium max-w-[200px] truncate">{b.title}</td>
                <td className="p-3 text-stone-500 font-mono text-xs">{b.slug}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${b.status === "published"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-700"
                      }`}
                  >
                    {b.status || "published"}
                  </span>
                </td>
                <td className="p-3 text-stone-500 text-xs">{formatDate(b.created_at)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing(b)}
                    className="text-stone-700 mr-3"
                    data-testid={`edit-blog-${b.id}`}
                  >
                    <Edit2 className="w-4 h-4 inline" />
                  </button>
                  <button
                    onClick={() => del(b.id)}
                    className="text-red-700"
                    data-testid={`delete-blog-${b.id}`}
                  >
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <BlogEditor
          item={editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BlogEditor({ item, onClose }) {
  const isNew = !item.id;
  const [f, setF] = useState({
    title: item.title || "",
    content: item.content || "",
    featured_image: item.featured_image || "",
    status: item.status || "published",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const uploadImage = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((prev) => ({ ...prev, featured_image: BACKEND + data.url }));
    } catch (e) { alert(formatError(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { setErr("Title is required."); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/blogs", f);
      } else {
        await api.put(`/admin/blogs/${item.id}`, f);
      }
      onClose();
    } catch (e) { setErr(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="space-y-4" data-testid="blog-editor">
        <div className="font-display text-xl md:text-2xl">
          {isNew ? "Add Blog Post" : "Edit Blog Post"}
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 px-3 py-2 rounded-md">{err}</div>}

        <Field label="Title" v={f.title} on={(v) => setF({ ...f, title: v })} />

        <Select
          label="Status"
          v={f.status}
          on={(v) => setF({ ...f, status: v })}
          options={["published", "draft"]}
        />

        {/* Featured Image */}
        <div>
          <div className="ols-label mb-1">Featured Image</div>
          {f.featured_image && (
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-stone-100 mb-2">
              <img src={f.featured_image} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => setF({ ...f, featured_image: "" })}
                className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full text-[10px] flex items-center justify-center"
              >
                ×
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="url"
              value={f.featured_image}
              onChange={(e) => setF({ ...f, featured_image: e.target.value })}
              placeholder="Paste image URL…"
              className="flex-1 border border-stone-300 px-3 py-2 text-sm outline-none rounded-md"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-sm rounded-md hover:bg-stone-50"
            >
              <Upload className="w-3.5 h-3.5" /> Upload
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadImage(e.target.files)}
          />
        </div>

        {/* Content */}
        <div>
          <div className="ols-label mb-1">Content</div>
          <ReactQuill
            theme="snow"
            value={f.content}
            onChange={(value) => setF({ ...f, content: value })}
            className="bg-white [&_.ql-editor]:min-h-[250px]"
            placeholder="Write your content here..."
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-outline">
            Cancel
          </button>
          <button className="btn-primary" disabled={saving} data-testid="save-blog">
            {saving ? "Saving…" : isNew ? "Publish Post" : "Save Changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// APARTMENTS (Members-only listings)
// =============================================================================
function AdminApartments() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/apartments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this apartment?")) return;
    try {
      await api.delete(`/admin/apartments/${id}`);
      load();
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <div data-testid="admin-apartments">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Apartments in Goa</h1>
        <button className="btn-primary" onClick={() => setEditing({})} data-testid="new-apartment">
          <Plus className="w-4 h-4" /> New Apartment
        </button>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr className="text-left">
              <th className="p-3">Name</th>
              <th className="p-3">Location</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Price/month</th>
              <th className="p-3">Status</th>
              <th className="p-3">Images</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-stone-500">No apartments yet. Click "New Apartment" to add one.</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-stone-100" data-testid={`apartment-row-${a.id}`}>
                <td className="p-3 font-medium">{a.name}</td>
                <td className="p-3 text-stone-600">{a.location}</td>
                <td className="p-3">{a.contact_person}</td>
                <td className="p-3">{a.phone}</td>
                <td className="p-3">{a.price_per_month ? "₹" + a.price_per_month : "—"}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${a.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>{a.status}</span>
                </td>
                <td className="p-3">{(a.images || []).length}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEditing(a)} className="text-stone-700 hover:text-stone-900" data-testid={`edit-apartment-${a.id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(a.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-apartment-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ApartmentEditor item={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ApartmentEditor({ item, onClose }) {
  const isNew = !item.id;
  const [f, setF] = useState({
    name: item.name || "",
    location: item.location || "Goa",
    contact_person: item.contact_person || "",
    phone: item.phone || "",
    description: item.description || "",
    price_per_month: item.price_per_month || "",
    bedrooms: item.bedrooms || 1,
    bathrooms: item.bathrooms || 1,
    max_guests: item.max_guests || 2,
    amenities: item.amenities || [],
    images: item.images || [],
    status: item.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [amenity, setAmenity] = useState("");
  const inputRef = useRef(null);

  const uploadImage = async (files) => {
    if (!files || files.length === 0) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((p) => ({ ...p, images: [...p.images, data.url] }));
      } catch (e) { alert(formatError(e)); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        price_per_month: f.price_per_month,
        bedrooms: Number(f.bedrooms) || 0,
        bathrooms: Number(f.bathrooms) || 0,
        max_guests: Number(f.max_guests) || 0,
      };
      if (isNew) await api.post("/admin/apartments", payload);
      else await api.put(`/admin/apartments/${item.id}`, payload);
      onClose();
    } catch (e) { alert(formatError(e)); }
    setSaving(false);
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="apartment-editor-form">
        <div className="flex items-center justify-between">
          <div className="font-display text-xl">{isNew ? "New Apartment" : "Edit Apartment"}</div>
          <button type="button" onClick={onClose} className="text-stone-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" v={f.name} on={(v) => setF({ ...f, name: v })} />
          <Field label="Location" v={f.location} on={(v) => setF({ ...f, location: v })} />
          <Field label="Contact Person" v={f.contact_person} on={(v) => setF({ ...f, contact_person: v })} />
          <Field label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} />
          <Field label="Price / month (₹)" v={f.price_per_month} on={(v) => setF({ ...f, price_per_month: v })} />
          <Select label="Status" v={f.status} on={(v) => setF({ ...f, status: v })} options={["active", "inactive"]} />
          <Field label="Bedrooms" type="number" v={f.bedrooms} on={(v) => setF({ ...f, bedrooms: v })} />
          <Field label="Bathrooms" type="number" v={f.bathrooms} on={(v) => setF({ ...f, bathrooms: v })} />
          <Field label="Max Guests" type="number" v={f.max_guests} on={(v) => setF({ ...f, max_guests: v })} />
        </div>
        <Field label="Description" v={f.description} on={(v) => setF({ ...f, description: v })} textarea />

        <div>
          <div className="ols-label mb-1">Amenities</div>
          <div className="flex flex-wrap gap-2 mb-2">
            {(f.amenities || []).map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-stone-100 px-2 py-0.5 rounded-full text-xs">
                {a}
                <button type="button" onClick={() => setF({ ...f, amenities: f.amenities.filter((_, x) => x !== i) })} className="text-stone-500 hover:text-red-600">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={amenity} onChange={(e) => setAmenity(e.target.value)} placeholder="Add amenity" className="flex-1 border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" />
            <button type="button" onClick={() => { if (amenity.trim()) { setF({ ...f, amenities: [...f.amenities, amenity.trim()] }); setAmenity(""); } }} className="btn-outline text-sm">Add</button>
          </div>
        </div>

        <div>
          <div className="ols-label mb-1">Images</div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {(f.images || []).map((u, i) => (
              <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-stone-100">
                <img src={u} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setF({ ...f, images: f.images.filter((_, x) => x !== i) })} className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full text-[10px] flex items-center justify-center">×</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="url" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="Paste image URL…" className="flex-1 border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" />
            <button type="button" onClick={() => { if (imgUrl.trim()) { setF({ ...f, images: [...f.images, imgUrl.trim()] }); setImgUrl(""); } }} className="btn-outline text-sm">Add URL</button>
            <button type="button" onClick={() => inputRef.current?.click()} className="flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-sm rounded-md hover:bg-stone-50">
              <Upload className="w-3.5 h-3.5" /> Upload
            </button>
          </div>
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImage(e.target.files)} />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button className="btn-primary" disabled={saving} data-testid="save-apartment">{saving ? "Saving…" : isNew ? "Create" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// MEMBERSHIPS (Admin listing with filters)
// =============================================================================
function AdminMemberships() {
  const [items, setItems] = useState([]);
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);

  const loadPlans = async () => {
    try {
      const { data } = await api.get("/memberships/plans");
      setPlans(data.plans || []);
    } catch { setPlans([]); }
  };

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (plan) params.set("plan", plan);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    try {
      const { data } = await api.get(`/admin/memberships?${params.toString()}`);
      setItems(data);
    } catch { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [plan, status]);
  useEffect(() => { loadPlans(); }, []);

  const totalRevenue = items.reduce((s, m) => s + (m.amount || 0), 0);
  const activeCount = items.filter((m) => m.is_currently_active).length;

  return (
    <div data-testid="admin-memberships">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Memberships</h1>
      </div>

      <div className="mb-10">
        <h2 className="font-display text-lg mb-4 text-stone-700">Membership Plans Pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {plans.map((p) => (
            <div key={p.key} className="bg-white border border-stone-200 rounded-xl p-5 flex flex-col items-start">
              <div className="flex items-center justify-between w-full mb-1">
                <div className="font-bold text-lg">{p.name}</div>
                {p.tag && <div className="text-[10px] uppercase font-bold bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">{p.tag}</div>}
              </div>
              <div className="text-stone-500 text-sm mb-3">{p.duration_months} months duration</div>
              <div className="font-display text-2xl mb-4">{inr(p.amount)}</div>
              <ul className="text-xs text-stone-600 space-y-1 mb-5 flex-1 w-full">
                {(p.perks || []).slice(0, 3).map((perk, i) => <li key={i}>• {perk}</li>)}
                {(p.perks || []).length > 3 && <li className="text-stone-400">...and more</li>}
              </ul>
              <button onClick={() => setEditingPlan(p)} className="mt-auto btn-outline text-sm w-full"><Edit2 className="w-3 h-3 inline mr-1" /> Edit Plan</button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
        <Stat icon={Crown} label="Total Memberships" value={items.length} />
        <Stat icon={CalendarCheck} label="Currently Active" value={activeCount} />
        <Stat icon={IndianRupee} label="Revenue" value={inr(totalRevenue)} />
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-3 mb-4 flex flex-col md:flex-row gap-2 md:items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by name or email…"
          className="flex-1 border border-stone-300 px-3 py-2 text-sm outline-none rounded-md"
          data-testid="membership-search"
        />
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" data-testid="membership-filter-plan">
          <option value="">All plans</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
          <option value="platinum">Platinum</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" data-testid="membership-filter-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
        </select>
        <button onClick={load} className="btn-outline text-sm">Search</button>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr className="text-left">
              <th className="p-3">Member</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Purchased</th>
              <th className="p-3">Valid Till</th>
              <th className="p-3">Status</th>
              <th className="p-3">Payment ID</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-stone-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-stone-500">No memberships found.</td></tr>
            )}
            {!loading && items.map((m) => (
              <tr key={m.id} className="border-t border-stone-100" data-testid={`membership-row-${m.id}`}>
                <td className="p-3">
                  <div className="font-medium">{m.user_name || "—"}</div>
                  <div className="text-xs text-stone-500">{m.user_email}</div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${m.plan_key === "platinum" ? "bg-stone-900 text-white" :
                    m.plan_key === "gold" ? "bg-amber-100 text-amber-800" :
                      "bg-stone-100 text-stone-700"
                    }`}>{m.plan_name}</span>
                </td>
                <td className="p-3">{inr(m.amount || 0)}</td>
                <td className="p-3 text-stone-600">{m.purchase_date ? new Date(m.purchase_date).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-stone-600">{m.valid_till ? new Date(m.valid_till).toLocaleDateString() : "—"}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${m.is_currently_active ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                    {m.is_currently_active ? "Active" : m.status === "inactive" ? "Inactive" : "Expired"}
                  </span>
                </td>
                <td className="p-3 font-mono text-xs">{m.razorpay_payment_id || "—"}</td>
                <td className="p-3">
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Are you sure you want to ${m.status === "active" ? "deactivate" : "activate"} this membership?`)) return;
                      try {
                        await api.post(`/admin/memberships/${m.id}/toggle-status`);
                        load();
                      } catch (err) { alert(formatError(err)); }
                    }}
                    className={`text-xs font-semibold ${m.status === "active" ? "text-rose-600 hover:text-rose-800" : "text-emerald-600 hover:text-emerald-800"}`}
                  >
                    {m.status === "active" ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingPlan && (
        <Modal onClose={() => setEditingPlan(null)}>
          <h2 className="font-display text-xl mb-4">Edit {editingPlan.name} Plan</h2>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const payload = {
                name: e.target.plan_name.value,
                amount: parseFloat(e.target.amount.value),
                duration_months: parseInt(e.target.duration_months.value, 10),
                tag: e.target.tag.value,
                perks: e.target.perks.value.split("\n").map(s => s.trim()).filter(Boolean)
              };
              await api.put(`/admin/memberships/plans/${editingPlan.key}`, payload);
              setEditingPlan(null);
              loadPlans();
            } catch (err) { alert(formatError(err)); }
          }} className="space-y-4">
            <label className="block">
              <div className="ols-label mb-1">Plan Name</div>
              <input type="text" name="plan_name" defaultValue={editingPlan.name} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" required />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <div className="ols-label mb-1">Amount (₹)</div>
                <input type="number" step="0.01" name="amount" defaultValue={editingPlan.amount} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" required />
              </label>
              <label className="block">
                <div className="ols-label mb-1">Duration (Months)</div>
                <input type="number" name="duration_months" defaultValue={editingPlan.duration_months} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" required />
              </label>
            </div>
            <label className="block">
              <div className="ols-label mb-1">Tag (e.g. Starter)</div>
              <input type="text" name="tag" defaultValue={editingPlan.tag || ""} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" />
            </label>
            <label className="block">
              <div className="ols-label mb-1">Perks (one per line)</div>
              <textarea name="perks" rows={5} defaultValue={(editingPlan.perks || []).join("\n")} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" />
            </label>
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setEditingPlan(null)} className="btn-outline text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm">Save Changes</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}



// =============================================================================
// UI HELPERS
// =============================================================================
function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white max-w-2xl w-full p-5 md:p-6 rounded-xl max-h-[90vh] overflow-y-auto">{children}</div>
    </div>
  );
}
function Field({ label, v, on, textarea, type = "text" }) {
  return (
    <label className="block">
      <div className="ols-label mb-1">{label}</div>
      {textarea ? (
        <textarea value={v} onChange={(e) => on(e.target.value)} rows={3} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" />
      ) : (
        <input type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md" />
      )}
    </label>
  );
}
function Select({ label, v, on, options, placeholder }) {
  return (
    <label className="block">
      <div className="ols-label mb-1">{label}</div>
      <select value={v} onChange={(e) => on(e.target.value)} className="w-full border border-stone-300 px-3 py-2 text-sm outline-none rounded-md bg-white">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function KV({ label, value }) {
  return (
    <div>
      <div className="text-[0.65rem] text-stone-500 uppercase tracking-wider">{label}</div>
      <div className="mt-0.5">{value || "—"}</div>
    </div>
  );
}

// =============================================================================
// ADVENTURES
// =============================================================================
function AdminAdventures() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/adventures").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this adventure?")) return;
    try {
      await api.delete(`/admin/adventures/${id}`);
      load();
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <div data-testid="admin-adventures">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Adventures</h1>
        <button className="btn-primary" onClick={() => setEditing({ title: "", tagline: "", description: "", highlights: [], bestMoments: [], perfectFor: [], bottomText: "", image: "", order: 0 })} data-testid="new-adventure">
          <Plus className="w-4 h-4" /> New Adventure
        </button>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr className="text-left">
              <th className="p-3">Order</th>
              <th className="p-3">Image</th>
              <th className="p-3">Title</th>
              <th className="p-3">Tagline</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-t border-stone-100">
                <td className="p-3 font-medium">{a.order}</td>
                <td className="p-3">
                  {a.image && <img src={a.image} alt={a.title} className="w-12 h-12 object-cover rounded" />}
                </td>
                <td className="p-3 font-medium">{a.title}</td>
                <td className="p-3 text-stone-500">{a.tagline}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(a)} className="p-1.5 hover:bg-stone-100 rounded text-stone-600"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(a.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-stone-500">No adventures found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && <AdventureModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AdventureModal({ item, onClose, onSaved }) {
  const [d, setD] = useState(item);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const processArray = (val) => Array.isArray(val) ? val : (val || "").split("\n").map(s => s.trim()).filter(Boolean);
      const payload = {
        ...d,
        highlights: processArray(d.highlights),
        bestMoments: processArray(d.bestMoments),
        perfectFor: processArray(d.perfectFor),
        order: Number(d.order) || 0,
        image: (d.image || "").split("\n")[0].trim()
      };

      if (d.id) {
        await api.put(`/admin/adventures/${d.id}`, payload);
      } else {
        await api.post(`/admin/adventures`, payload);
      }
      onSaved();
    } catch (e) { alert(formatError(e)); }
    finally { setSaving(false); }
  };

  const toText = (arr) => Array.isArray(arr) ? arr.join("\n") : (arr || "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 sm:p-6 backdrop-blur-sm transition-opacity">
      <div className="bg-stone-50 rounded-2xl w-full max-w-4xl max-h-full flex flex-col overflow-hidden shadow-2xl ring-1 ring-white/10">

        {/* Header */}
        <div className="px-6 py-5 border-b border-stone-200 flex items-center justify-between shrink-0 bg-white">
          <div>
            <h2 className="font-display text-xl text-stone-900">{d.id ? "Edit Adventure" : "New Adventure"}</h2>
            <p className="text-sm text-stone-500 mt-0.5">{d.id ? "Update the details of this adventure." : "Create a new adventure offering."}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><X className="w-5 h-5 text-stone-400 hover:text-stone-900" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 space-y-6">

          {/* General Information */}
          <section className="bg-white p-5 sm:p-6 rounded-xl border border-stone-200 shadow-sm space-y-5">
            <div className="border-b border-stone-100 pb-3">
              <h3 className="font-semibold text-stone-800 text-base">General Information</h3>
              <p className="text-xs text-stone-500 mt-1">Basic details and descriptions for this adventure.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="ols-label block mb-1.5">Title</label>
                <input className="ols-input w-full bg-stone-50/50 focus:bg-white border border-stone-200 rounded-md p-[10px]" value={d.title || ""} onChange={e => setD({ ...d, title: e.target.value })} placeholder="e.g. Desert Safari" />
              </div>
              <div>
                <label className="ols-label block mb-1.5">Tagline</label>
                <input className="ols-input w-full bg-stone-50/50 focus:bg-white border border-stone-200 rounded-md p-[10px]" value={d.tagline || ""} onChange={e => setD({ ...d, tagline: e.target.value })} placeholder="e.g. Jaisalmer" />
              </div>
            </div>
            <div>
              <label className="ols-label block mb-1.5">Description</label>
              <textarea className="ols-input w-full bg-stone-50/50 focus:bg-white border border-stone-200 rounded-md p-[10px]" rows={3} value={d.description || ""} onChange={e => setD({ ...d, description: e.target.value })} placeholder="A captivating description about the adventure..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="ols-label block mb-1.5">Display Order</label>
                <input type="number" className="ols-input w-full bg-stone-50/50 focus:bg-white border border-stone-200 rounded-md p-[10px]" value={d.order || 0} onChange={e => setD({ ...d, order: e.target.value })} placeholder="0" />
                <p className="text-[10px] text-stone-500 mt-1">Determines the sort order on the main page.</p>
              </div>
              <div>
                <label className="ols-label block mb-1.5">Bottom CTA Text</label>
                <input className="ols-input w-full bg-stone-50/50 focus:bg-white border border-stone-200 rounded-md p-[10px]" value={d.bottomText || ""} onChange={e => setD({ ...d, bottomText: e.target.value })} placeholder="e.g. Trade the city lights..." />
              </div>
            </div>
          </section>

          {/* Media */}
          <section className="bg-white p-5 sm:p-6 rounded-xl border border-stone-200 shadow-sm space-y-5">
            <div className="border-b border-stone-100 pb-3">
              <h3 className="font-semibold text-stone-800 text-base">Media</h3>
              <p className="text-xs text-stone-500 mt-1">Upload an engaging cover image.</p>
            </div>
            <div>
              <ImageUploader value={d.image || ""} onChange={val => setD({ ...d, image: val })} />
              <p className="text-[10px] text-stone-500 mt-2">Note: Only the first image is displayed for Adventures.</p>
            </div>
          </section>

          {/* Highlights & Details */}
          <section className="bg-white p-5 sm:p-6 rounded-xl border border-stone-200 shadow-sm space-y-5">
            <div className="border-b border-stone-100 pb-3">
              <h3 className="font-semibold text-stone-800 text-base">Lists & Details</h3>
              <p className="text-xs text-stone-500 mt-1">Add bullet points separated by a new line.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="ols-label block mb-1.5">Highlights</label>
                <textarea className="ols-input w-full bg-stone-50/50 focus:bg-white leading-relaxed border border-stone-200 rounded-md p-[10px]" rows={6} value={toText(d.highlights)} onChange={e => setD({ ...d, highlights: e.target.value })} placeholder="Camel safari across dunes&#10;Mesmerizing sunsets&#10;Stargazing..." />
              </div>
              <div>
                <label className="ols-label block mb-1.5">Best Moments</label>
                <textarea className="ols-input w-full bg-stone-50/50 focus:bg-white leading-relaxed border border-stone-200 rounded-md p-[10px]" rows={6} value={toText(d.bestMoments)} onChange={e => setD({ ...d, bestMoments: e.target.value })} placeholder="Golden hour photography&#10;Sunrise over dunes..." />
              </div>
              <div>
                <label className="ols-label block mb-1.5">Perfect For</label>
                <textarea className="ols-input w-full bg-stone-50/50 focus:bg-white leading-relaxed border border-stone-200 rounded-md p-[10px]" rows={6} value={toText(d.perfectFor)} onChange={e => setD({ ...d, perfectFor: e.target.value })} placeholder="Couples&#10;Families&#10;Photographers..." />
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-3 shrink-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2.5 text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2">
            {saving ? "Saving Changes..." : "Save Adventure"}
          </button>
        </div>

      </div>
    </div>
  );
}
